import type { FC, ReactNode } from "react";
import Table from "@/components/UI/Table/Table";
import type { TableColumn, TableRow } from "@/components/UI/Table/Table";
import type { ContactLensesData } from "@/types/config.types";
import { CustomSelect } from "@/components/CustomSelect";
import { Control, Controller, FieldValues } from "react-hook-form";
import styles from "./PrescriptionTable.module.css";
import { LensPrescription } from "@/types/prescription.types";
import tableStyles from "@/components/UI/Table/Table.module.css";
import { useTranslation } from "@/i18n";

const ATTRIBUTE_LABELS: Record<string, string> = {
  CL_SPHERE_POWER: "Power/Sphere",
  CL_DIAMETER: "DIA",
  CL_BASE_CURVE: "BC",
  CL_AXIS: "Axis",
  CL_ADDITION: "ADD",
  CL_CYLINDER: "CYL (Cylinder)",
  CL_DOMINANCE: "Dominance",
};

type PartialContactLensesData = Partial<ContactLensesData>;

interface PrescriptionTableProps {
  contactLensesData: PartialContactLensesData;
  control?: Control<FieldValues, any, FieldValues>;
  isEditable?: boolean;
  samePrescription?: boolean;
  modal?: boolean;
  highlightedAttribute?: string | null;
}

const getPrescriptionValue = (
  prescription: LensPrescription | null | undefined,
  attrKey: keyof LensPrescription,
): ReactNode => {
  if (!prescription) return "";
  return prescription[attrKey] ?? "";
};

export const PrescriptionTable: FC<PrescriptionTableProps> = ({
  contactLensesData,
  isEditable = true,
  control,
  samePrescription,
  modal = false,
  highlightedAttribute,
}) => {
  const { t } = useTranslation();

  const odAttrs: Record<string, any> =
    contactLensesData?.OD?.product?.attributes || {};
  const osAttrs: Record<string, any> =
    contactLensesData?.OS?.product?.attributes || {};
  // union of keys
  const attrKeys = Array.from(
    new Set([...Object.keys(odAttrs), ...Object.keys(osAttrs)]),
  );

  // Filter out CL_DOMINANCE if both eyes have only one value available
  const filteredAttrKeys = attrKeys.filter((key) => {
    if (key !== "CL_DOMINANCE") return true;
    const odValues = odAttrs[key];
    const osValues = osAttrs[key];
    const odHasSingleValue =
      (Array.isArray(odValues) && odValues.length === 1) ||
      odValues === undefined;
    const osHasSingleValue =
      (Array.isArray(osValues) && osValues.length === 1) ||
      osValues === undefined;
    return !(odHasSingleValue && osHasSingleValue);
  });

  const attributeOrder = [
    "CL_SPHERE_POWER",
    "CL_BASE_CURVE",
    "CL_DIAMETER",
    "CL_DOMINANCE",
    "CL_CYLINDER",
    "CL_AXIS",
    "CL_ADDITION",
  ];

  filteredAttrKeys.sort((a, b) => {
    const indexA = attributeOrder.indexOf(a);
    const indexB = attributeOrder.indexOf(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  const columns: TableColumn[] = [
    { header: "", key: "EYE" },
    ...filteredAttrKeys.map((k) => ({
      header: ATTRIBUTE_LABELS[k] || k,
      key: k,
    })),
  ];

  const rows: TableRow[] = [];

  const odPrescription = contactLensesData?.OD?.prescription;
  const osPrescription = contactLensesData?.OS?.prescription;

  const renderTableCell = (
    row: TableRow,
    attrs: Record<string, any>,
    prescription: LensPrescription | null | undefined,
    name: string,
    secondRow: boolean,
  ) => {
    filteredAttrKeys.forEach((k) => {
      const attrArray = attrs[k];
      const presVal = getPrescriptionValue(
        prescription,
        k as keyof LensPrescription,
      );

      if (Array.isArray(attrArray) && attrArray.length > 1 && isEditable) {
        const options = attrArray.map((v) => ({
          value: String(v),
          // if key is dominance i map the label to false:N and true:D
          label: k === "CL_DOMINANCE" ? (v === "False" ? "N" : "D") : String(v),
        }));
        const defaultValue = presVal ? String(presVal) : "";
        row[k] = (
          <Controller
            name={`${name}${k}`}
            control={control}
            rules={modal ? undefined : { required: true }}
            render={({
              field: { onChange, value, ref },
              fieldState: { error },
            }) => (
              <CustomSelect
                ref={ref}
                name={`${name}${k}`}
                options={options}
                value={value || defaultValue}
                onChange={onChange}
                disabled={samePrescription && name === "OS_"}
                error={error}
              />
            )}
          />
        );
      } else if (Array.isArray(attrArray) && attrArray.length === 1) {
        if (secondRow) {
          row[k] = (
            <span className={samePrescription ? styles.disabled : ""}>
              {String(attrArray[0])}
            </span>
          );
        } else {
          row[k] = String(attrArray[0]);
        }
      } else {
        if (secondRow) {
          row[k] = (
            <span className={samePrescription ? styles.disabled : ""}>
              {presVal}
            </span>
          );
        } else {
          row[k] = presVal;
        }
      }
    });
  };

  if (contactLensesData?.OD) {
    const row: TableRow = { EYE: t("product.eye.right") };
    renderTableCell(row, odAttrs, odPrescription, "OD_", false);
    rows.push(row);
  }

  if (contactLensesData?.OS) {
    const row: TableRow = { EYE: t("product.eye.left") };
    renderTableCell(row, osAttrs, osPrescription, "OS_", true);
    rows.push(row);
  }
  return (
    <Table
      columns={columns}
      rows={rows}
      highlightedColumn={highlightedAttribute}
      className={tableStyles.centeredTable}
    />
  );
};

export default PrescriptionTable;
